import React from "react";
import { IconButton, Typography, Box, Divider } from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import { useFieldArray } from "react-hook-form";

/**
 * Properties for the AddAnother component.
 * @typeParam T - The data type that one form instance's results will be placed within.
 */
export interface AddAnotherProps<T> {
  /**
   * The dot path to the React Hook Form data field that is the array.
   * @example
   * ```
   * // If you have a form with the following data schema:
   * type FormData = {
   *   schools: School[];
   * };
   *
   * type School = {
   *   name: string;
   *   address: string;
   * };
   *
   * // And you want to use <AddAnother /> for the "schools" field, then the arrayName would be "schools".
   * ```
   */
  arrayName: string;

  /**
   * The default values when a new form instance is added.
   */
  defaultValue: T;

  /**
   * A function which renders one entry of the form.
   */
  renderForm: AddAnotherRenderFormFn;
}

/**
 * A function which renders one instance of a form in the AddAnother component.
 * The index argument indicates what repetition of the form is being rendered. This should be templated into the React Hook Form data name. The index argument starts at 0.
 * The returned result should be a JSX tree with form elements. The AddAnother component will correctly set a unique key value for each element.
 * @example
 * ```
 * import { TextFieldRHF } from "./RHFWrappedComponents";
 *
 * const formRenderFn = (index: number): JSX.Element => (
 *   <>
 *     <TextFieldRHF textFieldLabel="School Name" name={`schools.${index}.name`} />
 *     <TextFieldRHF textFieldLabel="School Address" name={`schools.${index}.address`} />
 *   </>
 * );
 * ```
 */
export type AddAnotherRenderFormFn = (index: number) => JSX.Element;

/**
 * Allows a user to input many multiple field forms.
 * @typeParam T - The data type that one form instance's results will be placed within.
 */
export function AddAnother<T>({
  arrayName,
  defaultValue,
  renderForm,
}: AddAnotherProps<T>) {
  const { fields, append } = useFieldArray({ name: arrayName });

  // Adds an additional form to be rendered
  const onAddAnother = () => {
    append(defaultValue);
  };

  return (
    <>
      {fields.map((field, i) => (
        <React.Fragment key={field.id}>
          {renderForm(i)}

          {fields.length > 1 && i !== fields.length - 1 && <Divider />}
        </React.Fragment>
      ))}

      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
        }}
      >
        <IconButton onClick={onAddAnother} color="primary">
          <AddCircleOutline />
          <Typography
            sx={{
              position: "relative",
              top: "0.1rem",
              marginLeft: "0.4rem",
              fontWeight: "bold",
            }}
          >
            Add Another
          </Typography>
        </IconButton>
      </Box>
    </>
  );
}
